// Sound schlägt Grafik (Konzept §12.1 Punkt 3). Alles WebAudio-synthetisiert,
// keine Dateien. Ein Oszillator/Rauschloop pro Dauerklang, der moduliert wird —
// nicht pro Ereignis ein neuer Graph. Kurze Einmalklänge räumen sich selbst ab
// (onended → disconnect), sonst wachsen die Nodes ins Unendliche.
//
// Horn-Lüge (behoben): Früher feuerte das Horn bei jedem Anfahren/Fahren des
// Zuges — auch wenn er am anderen Ende der Strecke war. Der Spieler hörte ein
// Horn ohne sichtbaren Zug. Jetzt gilt: Horn nur, wenn ein Zug in Hörweite ist
// (schedule.hornAudibleAt / approachCueAt). Annäherung: Grollen → Singen → Horn.

import { featureAt, stationAt, STATIONS, trackPoint, BRANCH_MAIN } from '../../shared/track.js';
import {
  approachCueAt, hornAudibleAt, yardActivityAt, realSpeedFrom,
  TRAIN_HORN_RANGE_M, allTrainsAt,
} from '../../shared/schedule.js';

/** Typischer Schienenstoß-Abstand (m). TrainSystem nutzt dieselbe Größenordnung. */
export const JOINT_SPACING_M = 18.5;
/** Zeitversatz zwischen den beiden Drehgestellen eines Waggons (s bei 1 m/s). */
export const BOGIE_GAP_M = 8.5;

/** Hörweite Horn — re-export für Tests. */
export { TRAIN_HORN_RANGE_M };

// ---------------------------------------------------------------------------
// Reine Logik
// ---------------------------------------------------------------------------

/**
 * Primäre Klack-Rate (Hz) aus sichtbarer Geschwindigkeit.
 * Stillstand → 0. Proportional zu realSpeed (m/s).
 */
export function jointClickRate(realSpeed, spacingM = JOINT_SPACING_M) {
  const v = Number(realSpeed) || 0;
  if (v <= 0.05) return 0;
  return v / spacingM;
}

/** Sekundärschlag des hinteren Drehgestells, verzögert um bogieGap/speed. */
export function bogieDelaySec(realSpeed, bogieGapM = BOGIE_GAP_M) {
  const v = Math.max(Number(realSpeed) || 0, 0.05);
  return bogieGapM / v;
}

/**
 * Horn-Lautstärke aus Distanz (rein, testbar).
 * Jenseits TRAIN_HORN_RANGE_M → 0 — das ist die Anti-Lüge-Garantie.
 */
export function hornGainForDistance(distanceM, rangeM = TRAIN_HORN_RANGE_M) {
  const d = Number(distanceM);
  if (!Number.isFinite(d) || d < 0) return 0;
  if (d > rangeM) return 0;
  // Nah: voll, dann quadratisch abfallend.
  const t = 1 - d / rangeM;
  return t * t;
}

/** Einfacher String-Hash für deterministische Horn-Slots. */
function hashStr(s) {
  let h = 2166136261;
  const str = String(s ?? '');
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Reine Horn-Entscheidung für Tests: darf bei diesem Hörer/Zug gespielt werden?
 * (Spiegel der Laufzeitlogik ohne WebAudio.)
 */
export function shouldPlayHorn(trainState, listenerX, listenerY, listenerZ) {
  return hornAudibleAt(trainState, listenerX, listenerY, listenerZ);
}

// ---------------------------------------------------------------------------
// AudioSystem
// ---------------------------------------------------------------------------

export class AudioSystem {
  async init(ctx) {
    this.ctx = ctx;
    this.ready = false;
    this.failed = false;
    this.masterVol = 0.85;

    // Dauerzustand (moduliert, nicht neu erzeugt)
    this._jointPhase = 0;
    this._bogieArmed = false;
    this._bogieAt = 0;
    this._lastJolt = 0;
    // Deterministischer Horn-Takt aus Weltzeit, nicht Math.random — sonst
    // hören zwei Clients verschiedene Hörner (Konzept §5).
    this._hornCooldown = 0;
    this._lastHornTrainKey = '';
    this._prevTrainStates = new Map(); // id → state string
    this._stepPhase = 0;
    this._noiseWant = 0;
    this._oneShots = 0;
    this.MAX_ONESHOTS = 24;

    this.ac = null;
    this.master = null;
    this.nodes = null;

    // Autoplay-Policy: erst nach Interaktion / ctx.started.
    this._onGesture = () => this._tryStart(ctx);
    window.addEventListener('pointerdown', this._onGesture, { passive: true });
    window.addEventListener('keydown', this._onGesture, { passive: true });
  }

  update(dt, ctx) {
    if (this.failed) return;
    if (!this.ready) {
      if (ctx.started) this._tryStart(ctx);
      return;
    }
    if (this.ac.state === 'suspended') {
      this.ac.resume().catch(() => {});
    }

    const train = ctx.train;
    const ps = ctx.playerState;
    const weather = ctx.weather ?? { rain: 0, wind: 0 };
    const worldSeconds = ctx.worldSeconds ?? 0;
    const realSpeed = train?.realSpeed ?? 0;
    const s = ps?.trackS ?? train?.headS ?? 0;
    const branch = ps?.branch ?? train?.branch ?? BRANCH_MAIN;
    const feature = featureAt(s, branch);
    const station = stationAt(s, branch)
      ?? (train?.station ? STATIONS.find((x) => x.id === train.station) : null);

    // Hörerposition: Spielerwelt, sonst Kamera/Zugnähe.
    const listener = this._listenerPos(ps, train, s, branch);

    // Exposition: drinnen dumpfer (Konzept: Tiefpass beim Boxcar-Betreten).
    const mode = ps?.mode ?? 'ground';
    const inside = mode === 'inside';
    const onCar = !!(ps?.onCarId);
    let exposure = 1;
    if (inside) exposure = 0.12;
    else if (onCar && train?.carById) {
      const car = train.carById(ps.onCarId);
      exposure = car?.type?.exposure ?? 0.65;
    } else if (mode === 'ground' || mode === 'air') {
      exposure = 0.85;
    }

    // Annäherungs-Cue aus Fahrplan (alle Züge), nicht nur ctx.train.
    const cue = approachCueAt(worldSeconds, listener.x, listener.y, listener.z);
    // Joints: nur wenn man selbst auf/am fahrenden Zug ist — sonst das
    // entfernte Rattern über sing/rumble (sonst hört man Schienenstöße vom
    // Nordende).
    const jointSpeed = onCar || (cue.distance < 40 && cue.train?.state === 'moving')
      ? realSpeed
      : 0;

    this._updateJoints(dt, jointSpeed, feature, inside, onCar);
    this._updateWind(realSpeed, exposure, weather.wind ?? 0, inside);
    this._updateRain(weather.rain ?? 0, inside, onCar);
    this._updateYard(station, s, dt, worldSeconds, cue);
    this._updateApproach(cue, exposure, inside);
    this._updateHorn(dt, worldSeconds, listener, cue);
    this._updateJolt(train?.jolt ?? 0);
    this._updateSteps(dt, ps, onCar, inside);
    this._updateRadioNoise(dt);
  }

  /** Hörer in Weltkoordinaten — für Distanz zu Zügen. */
  _listenerPos(ps, train, trackS, branch) {
    if (ps && Number.isFinite(ps.x) && Number.isFinite(ps.z)) {
      return { x: ps.x, y: ps.y ?? 0, z: ps.z };
    }
    // Fallback: Gleisposition des Spielers/Zuges.
    const p = trackPoint(trackS ?? 0, { x: 0, y: 0, z: 0 }, branch ?? BRANCH_MAIN);
    return { x: p.x, y: p.y + 1.6, z: p.z };
  }

  // --- Öffentliche API ------------------------------------------------------

  /** Funkrauschen 0..1 — vom RadioSystem. */
  noise(level) {
    this._noiseWant = Math.max(0, Math.min(1, Number(level) || 0));
  }

  /** Kameraverschluss (PhotoSystem). */
  shutter() {
    if (!this.ready) return;
    this._clickBurst(1800, 0.25, 0.04);
    this._clickBurst(900, 0.12, 0.06, 0.03);
  }

  setMasterVolume(v) {
    this.masterVol = Math.max(0, Math.min(1, v));
    if (this.master) this.master.gain.value = this.masterVol;
  }

  // --- Start / Aufbau -------------------------------------------------------

  _tryStart(ctx) {
    if (this.ready || this.failed) return;
    if (!ctx?.started && !this._gestureSeen) {
      this._gestureSeen = true;
    }
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) {
        this.failed = true;
        return;
      }
      this.ac = new AC();
      this.master = this.ac.createGain();
      this.master.gain.value = this.masterVol;
      this.master.connect(this.ac.destination);
      this._buildGraph();
      this.ready = true;
      window.removeEventListener('pointerdown', this._onGesture);
      window.removeEventListener('keydown', this._onGesture);
    } catch (err) {
      console.warn('[audio] nicht verfügbar:', err);
      this.failed = true;
    }
  }

  /**
   * Dauergraph: ein Loop-Rauschen, das über Filter/Gains mehrfach angezapft
   * wird (Wind, Regen, Hof, Funk). Impulse für Stöße/Horn sind Einmalklänge.
   *
   * Feature-Färbung (Brücke/Einschnitt) bewusst mit Delay+Feedback+Filter
   * statt Convolver: Impulsantworten müssten synthetisch lang sein, fressen
   * CPU und klingen in Mono-First-Person oft matschig. Feedback-Delay gibt
   * „hohl/blechern" und „eng/reflektiert" mit zwei Parametern zuverlässig.
   */
  _buildGraph() {
    const ac = this.ac;
    const noiseBuf = this._makeNoiseBuffer(2.0);

    // Gemeinsame Rauschquelle (loop)
    const src = ac.createBufferSource();
    src.buffer = noiseBuf;
    src.loop = true;
    src.start();

    // --- Wind ---
    const windBp = ac.createBiquadFilter();
    windBp.type = 'bandpass';
    windBp.frequency.value = 600;
    windBp.Q.value = 0.7;
    const windLp = ac.createBiquadFilter();
    windLp.type = 'lowpass';
    windLp.frequency.value = 4000;
    const windGain = ac.createGain();
    windGain.gain.value = 0;
    src.connect(windBp);
    windBp.connect(windLp);
    windLp.connect(windGain);
    windGain.connect(this.master);

    // --- Regen ---
    const rainHp = ac.createBiquadFilter();
    rainHp.type = 'highpass';
    rainHp.frequency.value = 2500;
    rainHp.Q.value = 0.5;
    const rainGain = ac.createGain();
    rainGain.gain.value = 0;
    src.connect(rainHp);
    rainHp.connect(rainGain);
    rainGain.connect(this.master);

    // Blechdach-Regen (tiefere Resonanz, nur im Boxcar)
    const roofBp = ac.createBiquadFilter();
    roofBp.type = 'bandpass';
    roofBp.frequency.value = 900;
    roofBp.Q.value = 2.5;
    const roofGain = ac.createGain();
    roofGain.gain.value = 0;
    src.connect(roofBp);
    roofBp.connect(roofGain);
    roofGain.connect(this.master);

    // --- Hofbrummen ---
    const yardLp = ac.createBiquadFilter();
    yardLp.type = 'lowpass';
    yardLp.frequency.value = 180;
    const yardGain = ac.createGain();
    yardGain.gain.value = 0;
    src.connect(yardLp);
    yardLp.connect(yardGain);
    yardGain.connect(this.master);

    // --- Zug-Annäherung: Grollen (sehr tief) + Schienensingen (mittel) ---
    // Konzept §16.3 / Nutzerreport: lange vorher hörbar, Richtung über Pegel.
    const rumbleLp = ac.createBiquadFilter();
    rumbleLp.type = 'lowpass';
    rumbleLp.frequency.value = 90;
    const rumbleGain = ac.createGain();
    rumbleGain.gain.value = 0;
    src.connect(rumbleLp);
    rumbleLp.connect(rumbleGain);
    rumbleGain.connect(this.master);

    const singBp = ac.createBiquadFilter();
    singBp.type = 'bandpass';
    singBp.frequency.value = 420;
    singBp.Q.value = 1.8;
    const singGain = ac.createGain();
    singGain.gain.value = 0;
    src.connect(singBp);
    singBp.connect(singGain);
    singGain.connect(this.master);

    // --- Funkrauschen ---
    const radioBp = ac.createBiquadFilter();
    radioBp.type = 'bandpass';
    radioBp.frequency.value = 1400;
    radioBp.Q.value = 0.8;
    const radioGain = ac.createGain();
    radioGain.gain.value = 0;
    src.connect(radioBp);
    radioBp.connect(radioGain);
    radioGain.connect(this.master);

    // --- Schienenstoß-Bus (Filter + Delay-Färbung) ---
    const jointIn = ac.createGain();
    jointIn.gain.value = 1;
    const jointFilter = ac.createBiquadFilter();
    jointFilter.type = 'lowpass';
    jointFilter.frequency.value = 1200;
    const jointBody = ac.createBiquadFilter();
    jointBody.type = 'peaking';
    jointBody.frequency.value = 180;
    jointBody.Q.value = 1.2;
    jointBody.gain.value = 6;
    const jointGain = ac.createGain();
    jointGain.gain.value = 0.55;

    // Delay für Brücke (hohl) / Einschnitt (kurz, eng)
    const jointDelay = ac.createDelay(0.25);
    jointDelay.delayTime.value = 0.02;
    const jointFb = ac.createGain();
    jointFb.gain.value = 0.0;
    const jointWet = ac.createGain();
    jointWet.gain.value = 0.0;

    jointIn.connect(jointFilter);
    jointFilter.connect(jointBody);
    jointBody.connect(jointGain);
    jointGain.connect(this.master);

    jointBody.connect(jointDelay);
    jointDelay.connect(jointFb);
    jointFb.connect(jointDelay);
    jointDelay.connect(jointWet);
    jointWet.connect(this.master);

    // Metall-Tick-Oszillator-Pool-Ersatz: ein kurzer Noise-Burst-Buffer
    this._tickBuf = this._makeTickBuffer();
    this._thumpBuf = this._makeThumpBuffer();

    this.nodes = {
      noiseSrc: src,
      windBp, windLp, windGain,
      rainHp, rainGain, roofBp, roofGain,
      yardLp, yardGain,
      rumbleLp, rumbleGain,
      singBp, singGain,
      radioBp, radioGain,
      jointIn, jointFilter, jointBody, jointGain, jointDelay, jointFb, jointWet,
    };
  }

  _makeNoiseBuffer(seconds) {
    const ac = this.ac;
    const len = Math.floor(ac.sampleRate * seconds);
    const buf = ac.createBuffer(1, len, ac.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    return buf;
  }

  /** Kurzer gefilterter Impuls für Schienenstöße. */
  _makeTickBuffer() {
    const ac = this.ac;
    const len = Math.floor(ac.sampleRate * 0.06);
    const buf = ac.createBuffer(1, len, ac.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) {
      const t = i / ac.sampleRate;
      const env = Math.exp(-t * 90);
      d[i] = (Math.random() * 2 - 1) * env;
    }
    return buf;
  }

  /** Tiefer Resonanzanteil (Drehgestell / Kupplung). */
  _makeThumpBuffer() {
    const ac = this.ac;
    const len = Math.floor(ac.sampleRate * 0.12);
    const buf = ac.createBuffer(1, len, ac.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) {
      const t = i / ac.sampleRate;
      const env = Math.exp(-t * 28);
      d[i] = Math.sin(2 * Math.PI * 75 * t) * env * 0.7
        + Math.sin(2 * Math.PI * 110 * t) * env * 0.3
        + (Math.random() * 2 - 1) * env * 0.15;
    }
    return buf;
  }

  // --- Einmalklänge: hart limitiert, immer disconnect -------------------

  _playBuf(buffer, dest, when, gainVal, rate = 1) {
    if (!this.ready || this._oneShots >= this.MAX_ONESHOTS) return;
    const ac = this.ac;
    const src = ac.createBufferSource();
    src.buffer = buffer;
    src.playbackRate.value = rate;
    const g = ac.createGain();
    g.gain.value = gainVal;
    src.connect(g);
    g.connect(dest);
    this._oneShots++;
    src.onended = () => {
      try { src.disconnect(); g.disconnect(); } catch { /* */ }
      this._oneShots = Math.max(0, this._oneShots - 1);
    };
    src.start(when);
  }

  _clickBurst(freq, gainVal, dur, delay = 0) {
    if (!this.ready || this._oneShots >= this.MAX_ONESHOTS) return;
    const ac = this.ac;
    const t0 = ac.currentTime + delay;
    const osc = ac.createOscillator();
    osc.type = 'square';
    osc.frequency.value = freq;
    const g = ac.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0001, gainVal), t0 + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    const f = ac.createBiquadFilter();
    f.type = 'bandpass';
    f.frequency.value = freq;
    f.Q.value = 1.5;
    osc.connect(f);
    f.connect(g);
    g.connect(this.master);
    this._oneShots++;
    osc.onended = () => {
      try { osc.disconnect(); f.disconnect(); g.disconnect(); } catch { /* */ }
      this._oneShots = Math.max(0, this._oneShots - 1);
    };
    osc.start(t0);
    osc.stop(t0 + dur + 0.02);
  }

  // --- Schienenstöße --------------------------------------------------------

  _updateJoints(dt, realSpeed, feature, inside, onCar) {
    const n = this.nodes;
    if (!n) return;

    // Feature-Färbung: Delay/Feedback statt Convolver (siehe _buildGraph).
    let delayT = 0.018;
    let fb = 0.05;
    let wet = 0.08;
    let filterHz = 1400;
    let bodyHz = 180;
    let amp = onCar || (realSpeed > 0.5) ? 1 : 0.35;

    if (feature?.type === 'bridge') {
      // hohl, hallig, blechern
      delayT = 0.055;
      fb = 0.42;
      wet = 0.55;
      filterHz = 2200;
      bodyHz = 140;
      amp *= 1.25;
    } else if (feature?.type === 'cutting') {
      // eng, reflektiert, kürzer
      delayT = 0.028;
      fb = 0.28;
      wet = 0.35;
      filterHz = 1600;
      bodyHz = 220;
      amp *= 1.1;
    } else if (feature?.type === 'embankment') {
      delayT = 0.02;
      fb = 0.1;
      wet = 0.12;
      amp *= 0.95;
    }

    if (inside) {
      filterHz *= 0.55;
      amp *= 0.7;
      wet *= 0.5;
    }

    n.jointDelay.delayTime.value = delayT;
    n.jointFb.gain.value = fb;
    n.jointWet.gain.value = wet;
    n.jointFilter.frequency.value = filterHz;
    n.jointBody.frequency.value = bodyHz;

    const rate = jointClickRate(realSpeed);
    if (rate <= 0) {
      this._jointPhase = 0;
      this._bogieArmed = false;
      return;
    }

    this._jointPhase += rate * dt;
    if (this._jointPhase >= 1) {
      this._jointPhase -= Math.floor(this._jointPhase);
      // ka-
      const g = 0.22 * amp;
      this._playBuf(this._tickBuf, n.jointIn, 0, g, 0.95 + Math.random() * 0.1);
      this._playBuf(this._thumpBuf, n.jointIn, 0, g * 0.7, 0.9 + Math.random() * 0.15);
      // -tack (zweites Drehgestell)
      this._bogieArmed = true;
      this._bogieAt = bogieDelaySec(realSpeed);
    }

    if (this._bogieArmed) {
      this._bogieAt -= dt;
      if (this._bogieAt <= 0) {
        this._bogieArmed = false;
        const g = 0.18 * amp;
        this._playBuf(this._tickBuf, n.jointIn, 0, g, 1.0 + Math.random() * 0.08);
        this._playBuf(this._thumpBuf, n.jointIn, 0, g * 0.55, 1.05);
      }
    }
  }

  // --- Wind / Regen / Hof ---------------------------------------------------

  _updateWind(realSpeed, exposure, weatherWind, inside) {
    const n = this.nodes;
    if (!n) return;
    const speed01 = Math.min(1.4, realSpeed / 18);
    const level = (speed01 * 0.55 + weatherWind * 0.2) * exposure;
    const target = inside ? level * 0.25 : level;
    // Sanft gleiten — kein Zipper-Noise
    const g = n.windGain.gain;
    g.value += (target * 0.35 - g.value) * 0.08;

    const freq = inside
      ? 280 + speed01 * 200
      : 450 + speed01 * 1400 + weatherWind * 400;
    n.windBp.frequency.value = freq;
    n.windLp.frequency.value = inside ? 900 : 2500 + speed01 * 3500;
  }

  _updateRain(rain, inside, onCar) {
    const n = this.nodes;
    if (!n) return;
    const r = Math.max(0, Math.min(1, rain));
    if (inside) {
      // Blechdach: dichter, resonanter
      n.rainGain.gain.value += (0 - n.rainGain.gain.value) * 0.1;
      n.roofGain.gain.value += (r * 0.28 - n.roofGain.gain.value) * 0.1;
      n.roofBp.frequency.value = 700 + r * 400;
    } else {
      n.roofGain.gain.value += (0 - n.roofGain.gain.value) * 0.1;
      const open = onCar ? 0.22 : 0.18;
      n.rainGain.gain.value += (r * open - n.rainGain.gain.value) * 0.1;
      n.rainHp.frequency.value = 1800 + r * 1200;
    }
  }

  _updateYard(station, s, dt, worldSeconds = 0, cue = null) {
    const n = this.nodes;
    if (!n) return;
    // activity: Südhof belebt, Nordhof halb tot (Konzept §11)
    let activity = 0;
    if (station) {
      activity = station.activity ?? 0.3;
    }
    // Fahrplan-Anzeichen: kurz vor Abfahrt / bei Ankunft mehr Rangierlärm.
    let yardBoost = 0;
    if (station?.id && worldSeconds != null) {
      const ya = yardActivityAt(worldSeconds, station.id);
      if (ya.phase === 'pre-depart') yardBoost = 0.55;
      else if (ya.phase === 'arrival-soon') yardBoost = 0.35;
      else if (ya.phase === 'train-present') yardBoost = 0.2;
    }
    const target = (activity * 0.12 + yardBoost * 0.1);
    n.yardGain.gain.value += (target - n.yardGain.gain.value) * Math.min(1, dt * 1.5);

    // Gelegentliche entfernte Metallgeräusche im belebten Hof.
    // Rate aus Weltzeit-Hash statt Math.random → gleiche Klicks für alle (§5).
    const rate = (activity + yardBoost) * 0.1;
    if (rate > 0.05) {
      const tick = Math.floor(worldSeconds * 2);
      const h = (tick * 1103515245 + 12345) >>> 0;
      if ((h % 1000) / 1000 < dt * rate * 4) {
        this._metalClank(0.04 + (h % 60) / 1000);
      }
    }
  }

  /**
   * Fernes Grollen + Schienensingen aus approachCueAt.
   * Macht Warten zu Beobachten: man hört den Zug, lange bevor man ihn sieht.
   */
  _updateApproach(cue, exposure, inside) {
    const n = this.nodes;
    if (!n || !cue) return;
    const damp = inside ? 0.2 : exposure;
    const rTarget = (cue.rumble ?? 0) * 0.22 * damp;
    const sTarget = (cue.sing ?? 0) * 0.12 * damp;
    n.rumbleGain.gain.value += (rTarget - n.rumbleGain.gain.value) * 0.06;
    n.singGain.gain.value += (sTarget - n.singGain.gain.value) * 0.08;
    // Singen wird höher, je näher — grobe Entfernungsfärbung.
    if (cue.distance < Infinity) {
      const prox = Math.max(0, 1 - cue.distance / 900);
      n.singBp.frequency.value = 320 + prox * 280;
      n.rumbleLp.frequency.value = 70 + prox * 50;
    }
  }

  _metalClank(gainVal) {
    if (!this.ready || this._oneShots >= this.MAX_ONESHOTS) return;
    const ac = this.ac;
    const t0 = ac.currentTime;
    const freqs = [220, 340, 510];
    for (let i = 0; i < freqs.length; i++) {
      const osc = ac.createOscillator();
      osc.type = 'triangle';
      osc.frequency.value = freqs[i] * (0.97 + Math.random() * 0.06);
      const g = ac.createGain();
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(gainVal / (i + 1), t0 + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.35 + i * 0.05);
      osc.connect(g);
      g.connect(this.master);
      this._oneShots++;
      osc.onended = () => {
        try { osc.disconnect(); g.disconnect(); } catch { /* */ }
        this._oneShots = Math.max(0, this._oneShots - 1);
      };
      osc.start(t0);
      osc.stop(t0 + 0.5);
    }
  }

  // --- Horn -----------------------------------------------------------------

  /**
   * Horn nur, wenn ein Zug wirklich in Hörweite ist.
   * Früher: jedes Anfahren von ctx.train (auch 6 km entfernt) → Lüge.
   * Jetzt: allTrainsAt + hornAudibleAt; Gain fällt mit Distanz.
   */
  _updateHorn(dt, worldSeconds, listener, cue) {
    this._hornCooldown = Math.max(0, this._hornCooldown - dt);

    const trains = typeof allTrainsAt === 'function'
      ? allTrainsAt(worldSeconds)
      : [];

    for (const tr of trains) {
      const prev = this._prevTrainStates.get(tr.id);
      const state = tr.state ?? 'standing';
      // Anfahren: Signal — aber nur hörbar, wenn der Hörer nah genug ist.
      if (prev === 'standing' && state === 'moving') {
        if (hornAudibleAt(tr, listener.x, listener.y, listener.z)) {
          const d = cue?.train?.id === tr.id ? cue.distance : TRAIN_HORN_RANGE_M * 0.7;
          const gain = hornGainForDistance(d) * 0.55;
          if (gain > 0.02) this._playHorn(gain, 0.0, d);
        }
      }
      this._prevTrainStates.set(tr.id, state);
    }

    // Gelegentliches Fahrt-Horn: nur bewegter Zug in Hörweite, Cooldown,
    // deterministisch aus Weltzeit + Zug-id (kein Math.random).
    if (this._hornCooldown > 0) return;
    const movingNear = trains.filter((tr) => {
      if (tr.state !== 'moving') return false;
      const rs = realSpeedFrom(tr.speed);
      if (rs <= 3) return false;
      return hornAudibleAt(tr, listener.x, listener.y, listener.z);
    });
    if (movingNear.length === 0) return;

    // ~alle 40–70 Echtsekunden, phasenversetzt pro Zug.
    const tick = Math.floor(worldSeconds / 12); // ~1 Echtsekunde
    for (const tr of movingNear) {
      const seed = (hashStr(tr.id) ^ tick) >>> 0;
      // Selten: 1/50 pro Sekunde-Äquivalent in diesem Slot.
      if ((seed % 53) !== 0) continue;
      const d = (() => {
        // Distanz aus cue, falls gleicher Zug, sonst grob aus headS-Nähe.
        if (cue?.train?.id === tr.id) return cue.distance;
        return TRAIN_HORN_RANGE_M * 0.6;
      })();
      const gain = hornGainForDistance(d) * (0.22 + (seed % 15) / 100);
      if (gain > 0.02) {
        this._playHorn(gain, 0.1 + (seed % 20) / 100, d);
        this._hornCooldown = 28 + (seed % 25);
        break;
      }
    }
  }

  /**
   * Echtes Zughorn = Akkord (mehrere gestimmte Teiltöne), nicht ein Sinus.
   * Distanzdämpfung über Gain, grober Doppler über Teiltöne.
   */
  _playHorn(gainVal, doppler = 0, distanceM = 200) {
    if (!this.ready || this._oneShots >= this.MAX_ONESHOTS - 4) return;
    // Harte Kappe: nie jenseits der Hörweite abspielen (Doppelcheck).
    if (distanceM > TRAIN_HORN_RANGE_M) return;
    const ac = this.ac;
    const t0 = ac.currentTime;
    // US-Güterzug-typischer Moll-Akkord, grob
    const base = 155 * (1 - doppler * 0.08);
    const partials = [1, 1.25, 1.5, 2.0];
    // Dauer leicht aus Distanz (ferner = kürzer/matter), nicht random.
    const dur = 1.5 + Math.min(0.5, (900 - distanceM) / 900);

    for (let i = 0; i < partials.length; i++) {
      const osc = ac.createOscillator();
      osc.type = i === 0 ? 'sawtooth' : 'square';
      osc.frequency.value = base * partials[i];
      const f = ac.createBiquadFilter();
      f.type = 'lowpass';
      f.frequency.value = 900 - doppler * 200 - Math.min(300, distanceM * 0.25);
      const g = ac.createGain();
      const peak = gainVal * (0.35 / (i + 1));
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(Math.max(0.0001, peak), t0 + 0.08);
      g.gain.setValueAtTime(Math.max(0.0001, peak * 0.85), t0 + dur * 0.55);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
      osc.connect(f);
      f.connect(g);
      g.connect(this.master);
      this._oneShots++;
      osc.onended = () => {
        try { osc.disconnect(); f.disconnect(); g.disconnect(); } catch { /* */ }
        this._oneShots = Math.max(0, this._oneShots - 1);
      };
      osc.start(t0);
      osc.stop(t0 + dur + 0.05);
    }
  }

  // --- Kupplung / Schritte / Funk -------------------------------------------

  _updateJolt(jolt) {
    // Anstieg von jolt = Anfahren/Bremsen → Kupplungsstoß
    const dj = jolt - this._lastJolt;
    this._lastJolt = jolt;
    if (dj > 0.18 && jolt > 0.35) {
      this._playBuf(this._thumpBuf, this.master, 0, 0.2 + dj * 0.35, 0.7);
      this._metalClank(0.05 + dj * 0.08);
    }
  }

  _updateSteps(dt, ps, onCar, inside) {
    if (!ps || !ps.grounded) {
      this._stepPhase = 0;
      return;
    }
    const mode = ps.mode;
    if (mode === 'hang' || mode === 'climb' || mode === 'down') return;

    // Relative Gehgeschwindigkeit schätzen
    let sp = Math.hypot(ps.velocity?.x ?? 0, ps.velocity?.z ?? 0);
    if (onCar) {
      // Auf dem Zug steckt realSpeed in velocity — Anim ist ehrlicher.
      const a = ps.anim;
      sp = (a === 1) ? 2.2 : (a === 2) ? 4.5 : (a === 3) ? 1.0 : 0;
    }
    if (sp < 0.6) {
      this._stepPhase = 0;
      return;
    }

    const freq = sp < 3.5 ? 1.9 : 2.8;
    this._stepPhase += dt * freq * Math.min(1.6, sp / 2.2);
    if (this._stepPhase >= 1) {
      this._stepPhase -= 1;
      // Untergrund: Schotter / Blech / Erde
      if (onCar || inside) {
        // Waggonblech dröhnt
        this._playBuf(this._thumpBuf, this.master, 0, 0.06, 1.6 + Math.random() * 0.3);
        this._clickBurst(400 + Math.random() * 200, 0.04, 0.03);
      } else {
        const lateral = Math.abs(ps.lateral ?? 99);
        if (lateral < 3.2) {
          // Schotter knirscht
          this._playBuf(this._tickBuf, this.master, 0, 0.07, 1.8 + Math.random() * 0.4);
        } else {
          // Erde dumpf
          this._playBuf(this._thumpBuf, this.master, 0, 0.05, 0.55 + Math.random() * 0.15);
        }
      }
    }
  }

  _updateRadioNoise(dt) {
    const n = this.nodes;
    if (!n) return;
    const g = n.radioGain.gain;
    const target = this._noiseWant * 0.09;
    g.value += (target - g.value) * Math.min(1, dt * 6);
    // Körniger wenn laut (nichts zu hören): etwas breiterer Filter
    n.radioBp.Q.value = 0.5 + (1 - this._noiseWant) * 1.2;
    n.radioBp.frequency.value = 1200 + this._noiseWant * 800;
  }

  dispose() {
    window.removeEventListener('pointerdown', this._onGesture);
    window.removeEventListener('keydown', this._onGesture);
    try { this.ac?.close(); } catch { /* */ }
    this.ac = null;
    this.ready = false;
  }
}
